import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { CircularProgress } from '@mui/material';
import useLocales from 'hooks/useLocales';
import Button from 'components/Button/Index';
import Image from 'components/image/Image';
import CultureChart from 'components/cultureCart';
import CompetenciesPopup from 'components/shared/Popups/CompetenciesPopup/Index';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { JobService } from 'services/jobs/job';
import { MatchProfileService } from 'services/matchProfile/matchProfile';
import CompetenciesSelect, {
  getCompetenciesSelectFooter
} from 'components/shared/CompetenciesSelect';
import NoData from 'components/shared/NoData/Index';
import { useDebounce } from 'helpers/useDebounce';
import Pagination from '@material-ui/core/Pagination';
import './Index.scss';

const CultureProfile = (): JSX.Element => {
  const { t } = useLocales();
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [ids, setIds] = useState<string[]>([]);
  const [selectedCultureProfileId, setSelectedCultureProfileId] =
    useState<any>();
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({
    matching: ''
  });
  const [selectedCultureProfile, setSelectedCultureProfile] = useState<any>();

  const { cultureProfile }: any = useSelector(
    (state: RootState) => state.match
  );
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 500);

  const [openPopup, setOpenPopup] = useState<boolean>(false);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState<number>(1);
  const itemsPerPage = 6;
  const [loading, setLoading] = useState<boolean>(true);
  const [step, setStep] = useState(1);
  const [scores, setScores] = useState<{ [label: string]: number }>({});

  const { assessmentByGroup } = useSelector(
    (state: RootState) => state.assessment
  );

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  useEffect(() => {
    const filtered = cultureProfile?.results?.filter(
      (profile: any) => profile?.title?.toLowerCase().includes(debouncedQuery)
    );
    setFilteredData(filtered);
  }, [cultureProfile, debouncedQuery]);

  const handleCloseOpenPopup = (): void => {
    setSelectedCultureProfile(null);
    setSelectedCultureProfileId(null);
    setSelectedLabels([]);
    setFormValues({ matching: '' });
    setOpenPopup(!openPopup);
  };

  useEffect(() => {
    const selectedProfile = cultureProfile?.results?.find(
      (profile: any) => profile.id === selectedCultureProfileId
    );

    if (selectedProfile) {
      setSelectedCultureProfile(selectedProfile);
    }
  }, [cultureProfile, selectedCultureProfileId]);

  useEffect(() => {
    if (selectedCultureProfile) {
      setFormValues({ matching: selectedCultureProfile.title });
      setSelectedLabels(selectedCultureProfile?.competency_labels);
      const scoresObj: { [label: string]: number } = {};
      (selectedCultureProfile?.competency_labels || []).forEach(
        (label: string, idx: number) => {
          scoresObj[label] =
            selectedCultureProfile.competency_min_scores?.[idx] ?? 5;
        }
      );
      setScores(scoresObj);
      const a = selectedCultureProfile?.competencies?.map(
        (e: any) => e?.competency?.id
      );
      setIds(a);
    }
  }, [selectedCultureProfile]);

  const getApplications = () => {
    JobService.getAssessmentByGroup();
  };

  useEffect(() => {
    getMatchProfiles();
  }, [page, itemsPerPage]);

  const getMatchProfiles = async (): Promise<void> => {
    try {
      setLoading(true);
      setTimeout(async () => {
        await MatchProfileService.getMatchProfiles(
          true,
          itemsPerPage,
          (page - 1) * itemsPerPage
        );
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getApplications();
    setStep(1);
  }, []);

  const handleSavePopup = (
    selectedLabels: string[],
    scores: { [label: string]: number },
    formValues: any
  ) => {
    // Check for duplicate name (case-insensitive, ignore self in edit mode)
    const duplicate = cultureProfile?.results?.some(
      (profile: any) =>
        profile.title.trim().toLowerCase() ===
          formValues.matching.trim().toLowerCase() &&
        (!selectedCultureProfile || profile.id !== selectedCultureProfile.id)
    );
    if (duplicate) {
      setError('A culture profile with this name already exists.');
      return;
    }

    const allCompetencies = assessmentByGroup.flatMap(
      (group: any) => group.competencies
    );
    const nameToId: { [name: string]: number } = {};
    allCompetencies.forEach((c: any) => {
      nameToId[c.name] = c.id;
    });

    const reqBody = {
      title: formValues.matching,
      is_culture: true,
      competencies: selectedLabels.map((label) => ({
        competency: nameToId[label],
        min_score: scores[label] ?? 5
      }))
    };

    if (selectedCultureProfile && selectedCultureProfile.id) {
      MatchProfileService.editMatchProfile(
        selectedCultureProfile.id,
        true,
        reqBody
      );
    } else {
      MatchProfileService.createMatchProfile(true, reqBody);
    }
    getMatchProfiles();
    navigate('/company');
    setOpenPopup(false);
    setError(null);
    setStep(1);
  };

  const handleSetSelected = (label: string, id: any, text: string) => {
    const newSelected: string[] = [...selectedLabels];
    const newId: string[] = [...ids];

    const index = selectedLabels.indexOf(label);

    const indexID = ids.indexOf(id);

    if (index !== -1 && indexID !== -1) {
      newSelected.splice(index, 1);
      newId.splice(indexID, 1);
    } else if (newSelected.length < 5 && newId.length < 5) {
      newSelected.push(label);
      newId.push(id);
    } else if (newSelected.length < 5) {
      return;
    }
    setSelectedLabels(newSelected);
    setIds(newId);
  };

  return (
    <div className="container_block">
      <div className="culture_block">
        <span className="culture__left-title">{t('Culture Profile')}</span>
        <div className="search_bar">
          <div className="search_input">
            <Image
              className="btn-icon"
              name="search.svg"
              width={20}
              height={20}
            />

            <input
              type="text"
              className="search_input_field"
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <Button
            onClick={handleCloseOpenPopup}
            t={t}
            title={t('Create Culture Profile')}
            className="postjob__header-btn"
          />
        </div>
      </div>
      {loading ? (
        <div className="loadingContainer">
          <CircularProgress className="loading" />
        </div>
      ) : filteredData.length === 0 ? (
        <NoData />
      ) : (
        <div className="cart_item">
          {filteredData?.map((e: any) => (
            <CultureChart
              item={e}
              key={e.title}
              handleCloseOpenPopup={handleCloseOpenPopup}
              setSelectedCultureProfile={setSelectedCultureProfileId}
            />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
        <Pagination
          className="pagination"
          count={Math.ceil((cultureProfile?.count || 0) / itemsPerPage)}
          page={page}
          onChange={(_, value) => setPage(value)}
          variant="outlined"
          shape="rounded"
          showFirstButton
          showLastButton
        />
      </div>

      {openPopup && (
        <CompetenciesPopup
          className="popup-add-new"
          title={t('Please select the most important competencies for your culture profile.')}
          description="Select 5 competencies that best represent your company's culture, values, and expected behaviors. Focus on the qualities that define how your team works and what makes your company unique. You can always adjust your selection later to fine-tune your culture profile."
          onClose={() => {
            setError(null);
            handleCloseOpenPopup();
          }}
          footer={getCompetenciesSelectFooter({
            step,
            t,
            handleBack: () => setStep(1),
            handleSave: () =>
              handleSavePopup(selectedLabels, scores, formValues),
            handleNext: () => setStep(2),
            selectedList: selectedLabels
          })}
        >
          {error && (
            <div
              className="error-message"
              style={{ color: 'red', marginBottom: 16 }}
            >
              {error}
            </div>
          )}
          <CompetenciesSelect
            step={step}
            setStep={setStep}
            competencies={assessmentByGroup}
            selectedList={selectedLabels}
            onSelect={handleSetSelected}
            fromCulture
            matchProfile={true}
            formValues={formValues}
            setFormValues={setFormValues}
            scores={scores}
            setScores={setScores}
          />
        </CompetenciesPopup>
      )}
    </div>
  );
};

export default CultureProfile;
